import { BaseAction } from './ActionInterface';
import { ActionTypes } from './ActionTypes';
import { BeerState } from '../types';

/**
 * Toggle Favorite Action
 * Adds or removes a beer from the user's favorites list
 */
export class ToggleFavoriteAction extends BaseAction<string> {
  type = ActionTypes.TOGGLE_FAVORITE;
  
  execute(state: BeerState): BeerState {
    const beerId = this.payload;
    
    if (!beerId) {
      return state;
    }
    
    const favorites = [...state.userInteractions.favorites];
    const index = favorites.indexOf(beerId);
    
    let tastingList = [...state.userInteractions.tastingList];
    
    if (index === -1) {
      favorites.push(beerId);
      
      // If adding to favorites, remove from tasting list (beer has been tried)
      const tastingIndex = tastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        tastingList.splice(tastingIndex, 1);
      }
    } else {
      favorites.splice(index, 1);
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        favorites,
        tastingList,
      },
    };
  }
}

/**
 * Update Rating Action
 * Sets a rating for a specific beer
 */
export class UpdateRatingAction extends BaseAction<{beerId: string, rating: number}> {
  type = ActionTypes.UPDATE_RATING;
  
  execute(state: BeerState): BeerState {
    const { beerId, rating } = this.payload || { beerId: '', rating: 0 };
    
    if (!beerId) {
      return state;
    }
    
    const updatedRatings = {
      ...state.userInteractions.ratings,
      [beerId]: rating,
    };
    
    let tastingList = [...state.userInteractions.tastingList];
    
    // If rating is greater than 0, remove from tasting list (beer has been tried)
    if (rating > 0) {
      const tastingIndex = tastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        tastingList.splice(tastingIndex, 1);
      }
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        ratings: updatedRatings,
        tastingList,
      },
    };
  }
}

/**
 * Update Notes Action
 * Sets notes for a specific beer
 */
export class UpdateNotesAction extends BaseAction<{beerId: string, notes: string}> {
  type = ActionTypes.UPDATE_NOTES;
  
  execute(state: BeerState): BeerState {
    const { beerId, notes } = this.payload || { beerId: '', notes: '' };
    
    if (!beerId) {
      return state;
    }
    
    const updatedNotes = {
      ...state.userInteractions.notes,
      [beerId]: notes,
    };
    
    let tastingList = [...state.userInteractions.tastingList];
    
    // If notes are not empty, remove from tasting list (beer has been tried)
    if (notes && notes.trim() !== '') {
      const tastingIndex = tastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        tastingList.splice(tastingIndex, 1);
      }
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        notes: updatedNotes,
        tastingList,
      },
    };
  }
}

/**
 * Toggle Tasting List Action
 * Adds or removes a beer from the user's tasting list
 */
export class ToggleTastingListAction extends BaseAction<string> {
  type = ActionTypes.TOGGLE_TASTING_LIST;
  
  execute(state: BeerState): BeerState {
    const beerId = this.payload;
    
    if (!beerId) {
      return state;
    }
    
    const tastingList = [...state.userInteractions.tastingList];
    const index = tastingList.indexOf(beerId);
    
    if (index === -1) {
      tastingList.push(beerId);
    } else {
      tastingList.splice(index, 1);
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        tastingList,
      },
    };
  }
}

/**
 * Toggle Tried Action
 * Adds or removes a beer from the user's tried list
 */
export class ToggleTriedAction extends BaseAction<string> {
  type = ActionTypes.TOGGLE_TRIED;
  
  execute(state: BeerState): BeerState {
    const beerId = this.payload;
    
    if (!beerId) {
      return state;
    }
    
    const triedList = [...(state.userInteractions.triedList || [])];
    const index = triedList.indexOf(beerId);
    
    if (index === -1) {
      triedList.push(beerId);
    } else {
      triedList.splice(index, 1);
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        triedList,
      },
    };
  }
}

/**
 * Set User Interactions Action
 * Sets the entire user interactions state (used for initialization)
 */
export class SetUserInteractionsAction extends BaseAction<{
  favorites: string[];
  tastingList: string[];
  ratings: Record<string, number>;
  notes: Record<string, string>;
}> {
  type = ActionTypes.SET_USER_INTERACTIONS;
  
  execute(state: BeerState): BeerState {
    const userInteractions = this.payload;
    
    if (!userInteractions) {
      return state;
    }
    
    return {
      ...state,
      userInteractions: {
        ...state.userInteractions,
        ...userInteractions,
      },
    };
  }
}
